import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, readFileAsDataUrl } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, MoreHorizontal, Pencil, Trash2, FileDown, Image as ImageIcon, X, Building2, ExternalLink, Sparkles, Loader2, Send } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { ColorPicker } from '@/components/ColorPicker';

const emptyCompany = {
  name: '', legal_form: '', address: '', city: '', cap: '', province: '',
  vat_number: '', fiscal_code: '', phone: '', email: '', pec: '', website: '',
  rea: '', share_capital: '', primary_color: '#0F766E', accent_color: '#14B8A6',
  logo_data_url: '',
};

export default function Partners() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCompany);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [generatingBlank, setGeneratingBlank] = useState({}); // { [companyId]: true }
  const generateBlank = async (cid) => {
    if (generatingBlank[cid]) return;
    setGeneratingBlank(prev => ({ ...prev, [cid]: true }));
    const tId = toast.loading('Generazione PDF contratto stampabile vuoto in corso, attendere…');
    try {
      await api.openCompanyBlankPdf(cid);
      toast.success('PDF generato', { id: tId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore generazione PDF', { id: tId });
    } finally {
      setGeneratingBlank(prev => { const n = { ...prev }; delete n[cid]; return n; });
    }
  };

  const load = async () => {
    setLoading(true);
    try { setItems(await api.listCompanies()); } catch (e) { toast.error('Errore caricamento'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Il nome dell\'azienda è obbligatorio'); return; }
    try {
      if (editId) {
        await api.updateCompany(editId, form);
        toast.success('Azienda aggiornata');
      } else {
        await api.createCompany(form);
        toast.success('Azienda creata');
      }
      setOpen(false); setEditId(null); setForm(emptyCompany); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore salvataggio'); }
  };

  const handleEdit = (c) => { setEditId(c.id); setForm({ ...emptyCompany, ...c }); setOpen(true); };
  const handleNew = () => { setEditId(null); setForm(emptyCompany); setOpen(true); };
  const [seeding, setSeeding] = useState(false);
  const handleSeedEnergy4people = async () => {
    if (!window.confirm("Vuoi importare/aggiornare il listino zonizzato di Energy4people (132 prodotti + template voce personalizzata)? I prodotti standard esistenti verranno sostituiti; le voci personalizzabili verranno mantenute.")) return;
    setSeeding(true);
    try {
      const res = await api.seedEnergy4people();
      toast.success(`Listino importato: ${res.products_inserted} prodotti · azienda ${res.company_created ? 'creata' : 'aggiornata'}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore seed listino');
    } finally { setSeeding(false); }
  };

  const handleDelete = async (id) => {
    try { await api.deleteCompany(id); toast.success('Azienda eliminata'); load(); }
    catch (e) { toast.error('Errore eliminazione'); }
  };

  const handleLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error('Logo troppo grande (max 1.5MB)'); return; }
    const dataUrl = await readFileAsDataUrl(file);
    setForm(f => ({ ...f, logo_data_url: dataUrl }));
  };

  const filtered = items.filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader
        title="Aziende Partner"
        description="Aziende installatrici per le quali generare contratti white-label"
        actions={
          <div className="flex gap-2">
            <Button asChild variant="outline" data-testid="partners-onboarding-link">
              <Link to="/admin/onboarding"><Send className="size-4 mr-2" />Invita nuova azienda</Link>
            </Button>
            <Button variant="outline" onClick={handleSeedEnergy4people} disabled={seeding} data-testid="partners-seed-energy4people">
              {seeding ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Sparkles className="size-4 mr-2" />}
              Importa listino Energy4people
            </Button>
            <Button onClick={handleNew} data-testid="partners-create-button"><Plus className="size-4 mr-2" />Nuova azienda</Button>
          </div>
        }
      />

      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b flex items-center justify-between gap-3">
            <Input placeholder="Cerca azienda..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" data-testid="partners-search-input" />
            <div className="text-xs text-muted-foreground">{filtered.length} aziend{filtered.length === 1 ? 'a' : 'e'}</div>
          </div>
          {filtered.length === 0 ? (
            <div className="p-10 text-center">
              <div className="mx-auto size-12 rounded-full bg-secondary grid place-items-center mb-3"><Building2 className="size-5 text-muted-foreground" /></div>
              <div className="text-sm font-medium">{loading ? 'Caricamento...' : 'Nessuna azienda partner'}</div>
              {!loading && <div className="text-xs text-muted-foreground mb-4">Crea la prima azienda per iniziare</div>}
              {!loading && <Button onClick={handleNew} size="sm"><Plus className="size-4 mr-2" />Crea partner</Button>}
            </div>
          ) : (
            <Table data-testid="partners-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Azienda</TableHead>
                  <TableHead>Sede</TableHead>
                  <TableHead>P.IVA</TableHead>
                  <TableHead>Colori PDF</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(c => (
                  <TableRow key={c.id} data-testid={`partner-row-${c.id}`} className="cursor-pointer hover:bg-secondary/40" onClick={() => navigate(`/partners/${c.id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {c.logo_data_url ? (
                          <img src={c.logo_data_url} alt="" className="size-9 rounded border object-contain bg-white p-1" />
                        ) : (
                          <div className="size-9 rounded border bg-secondary grid place-items-center"><Building2 className="size-4 text-muted-foreground" /></div>
                        )}
                        <div>
                          <div className="font-medium hover:underline">{c.name}</div>
                          <div className="text-xs text-muted-foreground">{c.legal_form}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{[c.city, c.province].filter(Boolean).join(', ') || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{c.vat_number || '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="inline-block size-5 rounded border" style={{ background: c.primary_color }} title={c.primary_color} />
                        <span className="inline-block size-5 rounded border" style={{ background: c.accent_color }} title={c.accent_color} />
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`partner-actions-${c.id}`}><MoreHorizontal className="size-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/partners/${c.id}`)} data-testid={`partner-open-profile-${c.id}`}><ExternalLink className="size-4 mr-2" />Apri profilo</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(c)}><Pencil className="size-4 mr-2" />Modifica rapida</DropdownMenuItem>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={!!generatingBlank[c.id]} onClick={() => generateBlank(c.id)} data-testid={`partner-blank-pdf-${c.id}`}>
                            {generatingBlank[c.id] ? <Loader2 className="size-4 mr-2 animate-spin" /> : <FileDown className="size-4 mr-2" />}
                            {generatingBlank[c.id] ? 'Generazione in corso…' : 'PDF Contratto Stampabile Vuoto'}
                          </DropdownMenuItem>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive">
                                <Trash2 className="size-4 mr-2" />Elimina
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminare l’azienda?</AlertDialogTitle>
                                <AlertDialogDescription>Saranno eliminati anche i suoi prodotti a catalogo. Operazione irreversibile.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annulla</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(c.id)}>Elimina</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Modifica azienda' : 'Nuova azienda partner'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Ragione sociale *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Energia Verde S.r.l." data-testid="partner-name-input" /></div>
            <div className="space-y-2"><Label>Forma giuridica</Label>
              <Input value={form.legal_form} onChange={(e) => setForm({ ...form, legal_form: e.target.value })} placeholder="S.r.l. / S.p.A." /></div>
            <div className="space-y-2 md:col-span-2"><Label>Indirizzo</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Via Roma, 1" /></div>
            <div className="space-y-2"><Label>Città</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2"><Label>CAP</Label>
                <Input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} /></div>
              <div className="space-y-2"><Label>Provincia</Label>
                <Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} maxLength={2} /></div>
            </div>
            <div className="space-y-2"><Label>Partita IVA</Label>
              <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="font-mono" /></div>
            <div className="space-y-2"><Label>Codice Fiscale</Label>
              <Input value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} className="font-mono" /></div>
            <div className="space-y-2"><Label>Telefono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-2"><Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" /></div>
            <div className="space-y-2"><Label>PEC</Label>
              <Input value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} /></div>
            <div className="space-y-2"><Label>Sito web</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="www.example.it" /></div>
            <div className="space-y-2"><Label>REA</Label>
              <Input value={form.rea} onChange={(e) => setForm({ ...form, rea: e.target.value })} /></div>
            <div className="space-y-2"><Label>Capitale sociale (€)</Label>
              <Input value={form.share_capital} onChange={(e) => setForm({ ...form, share_capital: e.target.value })} /></div>

            <div className="md:col-span-2 space-y-3 border-t pt-4">
              <Label className="text-base font-heading">Branding PDF</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Colore primario</Label>
                  <ColorPicker value={form.primary_color} onChange={(c) => setForm({ ...form, primary_color: c })} testid="partner-primary-color-picker" /></div>
                <div className="space-y-2"><Label>Colore accent</Label>
                  <ColorPicker value={form.accent_color} onChange={(c) => setForm({ ...form, accent_color: c })} testid="partner-accent-color-picker" /></div>
              </div>
              <div className="space-y-2"><Label>Logo aziendale</Label>
                <div className="flex items-center gap-3">
                  <div className="size-20 rounded-lg border bg-white grid place-items-center overflow-hidden">
                    {form.logo_data_url ? <img src={form.logo_data_url} alt="logo" className="max-h-full max-w-full object-contain" /> : <ImageIcon className="size-6 text-muted-foreground" />}
                  </div>
                  <div className="flex-1">
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogo} className="text-xs" data-testid="partner-logo-upload" />
                    <div className="text-xs text-muted-foreground mt-1">PNG/JPEG/WebP - dimensioni consigliate 400x140px. Max 1.5MB.</div>
                  </div>
                  {form.logo_data_url && <Button type="button" variant="ghost" size="icon" onClick={() => setForm({ ...form, logo_data_url: '' })}><X className="size-4" /></Button>}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} data-testid="partner-save-button">{editId ? 'Salva' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
